/**
 * FormApprovalList - 待審批表單列表
 * 
 * 顯示當前用戶需要審批的所有表單
 */

import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n, getText } from '../../context/I18nContext';
import { usePendingApprovals } from '../../../hooks/useApi';
import DataTable from '../../components/DataTable';
import FormCard from '../../components/FormCard';

const FormApprovalList = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t, language } = useI18n(['common', 'search', 'approval']);

    // 用於處理多語言物件
    const getTextValue = (textObj) => getText(textObj, language);

    const { data: result, isLoading, error } = usePendingApprovals();
    const pendingForms = result?.data || [];
    const [visibleCount, setVisibleCount] = useState(10);
    const [isMobile, setIsMobile] = useState(false);
    const mobileEndRef = useRef(null);

    const formatFormType = useCallback((formId) => {
        return formId?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '-';
    }, []);

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('zh-TW', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        const colorMap = {
            pending: 'text-yellow-600 bg-yellow-50',
            approved: 'text-green-600 bg-green-50',
            rejected: 'text-red-600 bg-red-50'
        };
        return colorMap[status] || 'text-gray-600 bg-gray-50';
    };

    const columns = useMemo(() => [
        {
            accessorKey: 'applicationId',
            header: t('search:requestNumber'),
            cell: info => (
                <Link
                    to={`/forms/approval/${info.getValue()}`}
                    className="text-blue-600 hover:underline font-medium"
                >
                    {info.getValue()}
                </Link>
            )
        },
        {
            accessorKey: 'formId',
            header: t('search:formType'),
            cell: info => formatFormType(info.getValue()),
            meta: { filterable: true, filterLabel: t('search:formType') },
            filterFn: 'multiSelect'
        },
        {
            accessorKey: 'applicant',
            header: t('search:applicant'),
            cell: info => {
                const applicant = info.getValue();
                return (
                    <div>
                        <div className="font-medium">{applicant?.displayName || '-'}</div>
                        <div className="text-xs text-gray-500">{applicant?.department}</div>
                    </div>
                );
            }
        },
        {
            id: 'currentStep',
            accessorFn: (row) => {
                const currentStep = row.approvalFlow?.find(s => s.status === 'current');
                const stepName = currentStep?.stepName;
                if (stepName && typeof stepName === 'object') {
                    return getTextValue(stepName);
                }
                return stepName || row.currentStepId || '-';
            },
            header: t('approval:list.currentStep'),
            cell: info => info.getValue() || '-',
            meta: { filterable: true, filterLabel: t('approval:list.currentStep') },
            filterFn: 'multiSelect'
        },
        {
            accessorKey: 'submittedAt',
            header: t('approval:list.submitDate'),
            cell: info => formatDate(info.getValue())
        },
        {
            accessorKey: 'updatedAt',
            header: t('approval:list.updateDate'),
            cell: info => formatDate(info.getValue())
        },
        {
            header: t('common:actions'),
            cell: info => (
                <button
                    onClick={() => navigate(`/forms/approval/${info.row.original.applicationId}`)}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                >
                    {t('approval:list.approveAction')}
                </button>
            )
        }
    ], [formatFormType, getTextValue, navigate, t]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 1024);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setVisibleCount(10);
    }, [pendingForms]);

    useEffect(() => {
        if (!isMobile) return;
        const node = mobileEndRef.current;
        if (!node) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && visibleCount < pendingForms.length) {
                    setVisibleCount(prev => Math.min(prev + 10, pendingForms.length));
                }
            },
            { rootMargin: '200px' }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [isMobile, pendingForms.length, visibleCount]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-8 max-w-7xl">
            {/* 頁面標題 */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">
                    {t('approval:list.title')}
                </h1>
                <p className="text-gray-600 mt-1">
                    {t('approval:list.description')}
                </p>
            </div>

            {/* 錯誤訊息 */}
            {error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                    {error.message || t('approval:list.loadFailed')}
                </div>
            )}

            {/* 表格 */}
            <div className="bg-white rounded-lg shadow-md">
                {pendingForms.length === 0 ? (
                    <div className="p-12 text-center text-gray-500">
                        <div className="text-6xl mb-4">✓</div>
                        <div className="text-lg font-medium">{t('approval:list.noForms')}</div>
                        <p className="mt-2">{t('approval:list.allProcessed')}</p>
                    </div>
                ) : (
                    <>
                        <div className="hidden lg:block">
                            <DataTable
                                data={pendingForms}
                                columns={columns}
                            />
                        </div>

                        <div className="lg:hidden space-y-3 p-4">
                            {pendingForms.slice(0, visibleCount).map((form) => {
                                const statusText = t('status.pending', { ns: 'common', defaultValue: 'Pending' });
                                return (
                                    <FormCard
                                        key={form.applicationId}
                                        title={form.applicationId}
                                        subtitle={formatFormType(form.formId)}
                                        status={statusText}
                                        statusClassName={`${getStatusColor(form.status || 'pending')} bg-gray-50 border border-gray-200`}
                                        meta={[
                                            { label: t('search:applicant'), value: form.applicant?.displayName || '-' },
                                            { label: t('approval:list.currentStep'), value: getTextValue(form.approvalFlow?.find(s => s.status === 'current')?.stepName) || form.currentStepId || '-' },
                                            { label: t('approval:list.submitDate'), value: formatDate(form.submittedAt) },
                                            { label: t('approval:list.updateDate'), value: formatDate(form.updatedAt) },
                                        ]}
                                        actions={[{
                                            label: t('approval:list.approveAction'),
                                            onClick: () => navigate(`/forms/approval/${form.applicationId}`),
                                            variant: 'primary'
                                        }]}
                                    />
                                );
                            })}
                            <div ref={mobileEndRef} className="flex items-center justify-center py-3 text-xs text-gray-500">
                                {visibleCount >= pendingForms.length && pendingForms.length > 0 ? 'End' : ''}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default FormApprovalList;
